import { onlineManager } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { Button, EmptyState } from '@/components/ui'
import { retryStalledQueries } from '@/lib/queryClient'

/**
 * Telling "nothing is there" apart from "we never got to ask".
 *
 * TanStack Query does not run a query while the browser reports itself
 * offline: the query sits at `fetchStatus: 'paused'`, still `isPending`, and
 * **`isLoading` is false** — because nothing is in flight. A screen that gates
 * its empty state on `isLoading` therefore renders the empty state, with
 * `data ?? []` behind it, and states as fact something it never asked.
 *
 * That is exactly what happened on Matt's phone on 2026-10-01: the app opened
 * on a dropped connection and the dashboard said "No budget codes for
 * FY2026-27 — set up this fiscal year" over a year holding eleven codes and a
 * cycle of charges. Reproduced here by going offline and selecting a year that
 * was not already cached: the query function was never called at all, and the
 * screen said the account was empty. Switching years is what cleared it, since
 * a new key starts a new query — which is why it looked like toggling fixed
 * something.
 *
 * The rule this encodes: **an empty state is a claim about data, and may only
 * be rendered once a query has actually answered.** Anything else — loading,
 * paused, offline — says so instead.
 */

export type LoadState = 'ready' | 'loading' | 'offline'

/** Reads TanStack's own idea of connectivity, which is what pauses queries. */
export function useIsOffline(): boolean {
  return useSyncExternalStore(
    (notify) => onlineManager.subscribe(notify),
    () => !onlineManager.isOnline(),
    () => false,
  )
}

interface GateableQuery {
  isPending: boolean
  fetchStatus?: 'fetching' | 'paused' | 'idle'
}

/**
 * The state of the screen as a whole, given every query it draws from.
 *
 * `isPending` rather than `isLoading`, deliberately: a paused query is pending
 * with nothing in flight, and that is the case this exists to catch.
 */
export function loadStateOf(...queries: readonly GateableQuery[]): LoadState {
  if (!queries.some((q) => q.isPending)) return 'ready'
  const paused = queries.some((q) => q.isPending && q.fetchStatus === 'paused')
  return paused || !onlineManager.isOnline() ? 'offline' : 'loading'
}

/**
 * What a screen shows instead of its content while it has no answer.
 *
 * Returns null when the state is 'ready', so a screen can render it directly
 * above the content it guards.
 */
export function LoadingOrOffline({
  state,
  onRetry = retryStalledQueries,
  what = 'This',
}: {
  state: LoadState
  onRetry?: () => void
  /** Named in the offline message — "Your budget", "Your receipts". */
  what?: string
}) {
  if (state === 'ready') return null

  if (state === 'loading') {
    return (
      <p className="text-slate-500" role="status">
        Loading…
      </p>
    )
  }

  return (
    <EmptyState title="No connection">
      <div className="space-y-3">
        <p>
          {what} can&apos;t load until you&apos;re back online. Nothing is lost
          — this is what the app can reach, not what you have.
        </p>
        <Button onClick={onRetry} variant="primary">
          Try again
        </Button>
      </div>
    </EmptyState>
  )
}
